import React from 'react';

import Section, { InfoCard, SectionHeader } from 'views/site/Section.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * Stakes
 *
 * The stakes, in the storefront's own language for this job: a centred header,
 * a contained panel of large tabular numerals divided by hairlines, a cited
 * source, then the standard icon-card grid for the specific culprits. Exactly
 * the shape of the storefront's RiskData section, because it is doing exactly
 * the same work.
 *
 * The content is the opening of the in-home presentation — "at 3:30 in the
 * morning, could you have a fire?" — but the drama is carried by the type
 * scale rather than by a dark ground. The page's dark budget is spent on the
 * trust strip, the walkthrough call to action, and the footer.
 *
 * Only the water figure carries a live citation, because it is the only one
 * with a public dataset behind it. The other two are attributed in the source
 * line without inventing a link.
 */

const Stakes = () => {

    const mobile = Utils.isMobile();

    const stats = [{
        label: 'to get everyone safely out of a fire in a modern home',
        note: 'Open plans, synthetic materials, higher fuel load',
        value: '3 min'
    },{
        label: 'of home fire deaths come from reports between 11pm and 7am',
        note: 'The hours nobody is awake to catch problems quickly',
        value: '50%'
    },{
        label: 'homes files a water or freeze claim every year',
        note: 'This is roughly 1.50 claims for every 100 insurance policies',
        value: '1 in 67'
    }];

    const culprits = [{
        copy: 'Furniture, curtains and floors are petroleum based, or wood chips and glue. Just about everything in a modern home burns faster and hotter than it used to.',
        icon: 'tv-fill',
        title: 'Your house is full of fuel'
    },{
        copy: 'Most people do not realize lint is highly flammable. It collects where nobody looks, behind a machine that runs while everyone is asleep or away from home.',
        icon: 'basket2',
        title: 'The dryer nobody thinks about'
    },{
        copy: 'Many house fires start here, which is also the one room a smoke alarm cannot sit in without going off every time you sear something.',
        icon: 'egg-fried',
        title: 'The kitchen, and its blind spot'
    },{
        copy: 'It starts as a headache and nausea that reads like the flu. By the time it grows to confusion and drowsiness, people are too disoriented to save themselves.',
        icon: 'cloud-haze2-fill',
        title: 'Carbon monoxide gives no warning'
    },{
        copy: 'A supply hose, a water heater, a line behind the fridge. No alarm sounds, nobody is hurt, and the damage runs into five figures.',
        icon: 'droplet-fill',
        title: 'Water does its damage quietly'
    },{
        copy: 'Exhaust gases, dust and swings in temperature mean a standard smoke alarm is not rated for a garage or an attic at all.',
        icon: 'building',
        title: 'The rooms nothing is watching'
    }];

    return (
        <Section id={'stakes'}>
            <SectionHeader
            eyebrow={'Why it matters'}
            subtitle={'Nobody is awake to smell smoke, see flame, or hear a single alarm two rooms away. That is the hour this equipment exists for. Everything on this page comes down to the few minutes between a fire starting and everybody being outside, and to how much of that time you get back.'}
            title={'At 3:30 in the morning, could you have a fire?'} />

            {/* One panel on desktop, separate boxes on mobile */}
            <div style={{
                backgroundColor: mobile ? 'transparent' : Theme.colors.offWhite,
                border: mobile ? 'none' : `1px solid ${Theme.colors.border()}`,
                borderRadius: 24,
                marginTop: mobile ? 8 : 16,
                padding: mobile ? 0 : '48px 32px'
            }}>
                <div style={{
                    display: 'grid',
                    gap: mobile ? 16 : 0,
                    gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)'
                }}>
                    {stats.map((stat, index) => (
                        <div
                        key={index}
                        style={{
                            borderLeft: (mobile === false && index > 0) ? `1px solid ${Theme.colors.border()}` : 'none',
                            padding: mobile ? '28px 24px' : '4px 32px',
                            textAlign: 'center',
                            ...mobile && {
                                backgroundColor: Theme.colors.offWhite,
                                border: `1px solid ${Theme.colors.border()}`,
                                borderRadius: 20
                            }
                        }}>
                            <div style={{
                                color: Theme.colors.text(),
                                fontFamily: Theme.fonts.heading,
                                fontSize: mobile ? 38 : 64,
                                fontVariantNumeric: 'tabular-nums',
                                fontWeight: 600,
                                letterSpacing: '-0.045em',
                                lineHeight: 1
                            }}>{stat.value}</div>
                            <div style={{
                                backgroundColor: Theme.colors.border(),
                                borderRadius: 2,
                                height: 3,
                                margin: mobile ? '14px auto 0' : '20px auto 0',
                                width: 40
                            }} />
                            <div style={{
                                ...Theme.textStyles.body({ color: Theme.colors.text(), fontSize: mobile ? 15 : 16, fontWeight: 500 }),
                                margin: mobile ? '14px auto 0' : '20px auto 0',
                                maxWidth: 260
                            }}>{stat.label}</div>
                            <div style={{
                                ...Theme.textStyles.caption({ color: Theme.colors.subText() }),
                                marginTop: 8
                            }}>{stat.note}</div>
                        </div>
                    ))}
                </div>
            </div>

            <span style={{
                ...Theme.textStyles.caption({ color: Theme.colors.lightGrey, textAlign: 'center' }),
                display: 'block',
                marginTop: 24
            }}>
                {'Fire figures from NFPA and UL fire-behaviour research. Water figures from the '}
                <a
                href={'https://www.iii.org/fact-statistic/facts-statistics-homeowners-and-renters-insurance'}
                rel={'noopener noreferrer'}
                style={{ color: Theme.colors.primary(), fontWeight: 500, textDecoration: 'none' }}
                target={'_blank'}>{'Insurance Information Institute'}</a>
                {' analysis of ISO homeowners data, 2019–2023 averages.'}
            </span>

            <div style={{ marginTop: mobile ? 56 : 88 }}>
                <div style={{
                    margin: '0 auto',
                    marginBottom: mobile ? 36 : 48,
                    maxWidth: 640,
                    textAlign: 'center'
                }}>
                    <span style={{
                        ...Theme.textStyles.heading({ fontSize: mobile ? 24 : 32, textAlign: 'center' }),
                        display: 'block',
                        marginBottom: 14
                    }}>{'It rarely starts where you are looking'}</span>
                    <span style={{
                        ...Theme.textStyles.sectionSubheading({ margin: '0 auto' }),
                        display: 'block'
                    }}>{'The hazards that hurt families are ordinary, and most of them sit in a room where a standard smoke alarm either is not allowed or would not help.'}</span>
                </div>

                <div style={{
                    display: 'grid',
                    gap: mobile ? 20 : 24,
                    gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)'
                }}>
                    {culprits.map((culprit, index) => (
                        <InfoCard key={index} {...culprit} />
                    ))}
                </div>
            </div>
        </Section>
    )
}

export default Stakes;
